import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublishedMagazines } from '@/lib/api';
import { BookOpen, Eye, Calendar, Lock, ArrowRight, Waves, Bell, BellRing, Download, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { usePushNotifications, useInstallPrompt } from '@/hooks/usePWA';

import CenturionFooter from '@/components/CenturionFooter';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_magzine-maker/artifacts/c7i8lwjh_image.png";

const HomePage = () => {
  const navigate = useNavigate();
  const [magazines, setMagazines] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isSubscribed, loading: pushLoading, supported: pushSupported, subscribe, unsubscribe } = usePushNotifications();
  const { isInstallable, isInstalled, install } = useInstallPrompt();

  useEffect(() => {
    const fetchMagazines = async () => {
      try {
        const res = await getPublishedMagazines();
        setMagazines(res.data);
      } catch {
        setMagazines([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMagazines();
  }, []);

  const handleNotificationToggle = async () => {
    if (isSubscribed) {
      const ok = await unsubscribe();
      if (ok) toast.success('Notifications turned off');
    } else {
      const ok = await subscribe();
      if (ok) toast.success('You\'ll be notified when new magazines are published!');
      else if (Notification.permission === 'denied') toast.error('Notifications blocked. Please enable in browser settings.');
    }
  };

  const handleInstall = async () => {
    const ok = await install();
    if (ok) toast.success('App installed! Find it on your home screen.');
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #004D6D 0%, #006994 20%, #00897B 40%, #26A69A 55%, #40E0D0 70%, #87CEEB 85%, #FFF8E1 100%)' }}>
      {/* Animated ocean waves overlay */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, #FFB347 0%, transparent 50%), radial-gradient(circle at 80% 20%, #FC8EAC 0%, transparent 50%), radial-gradient(circle at 50% 50%, #FFD166 0%, transparent 60%)' }} />
        <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 200" preserveAspectRatio="none" style={{ height: '120px' }}>
          <path fill="rgba(255,255,255,0.15)" d="M0,100 C320,180 440,60 720,100 C1000,140 1120,60 1440,100 L1440,200 L0,200 Z">
            <animate attributeName="d" dur="8s" repeatCount="indefinite" values="M0,100 C320,180 440,60 720,100 C1000,140 1120,60 1440,100 L1440,200 L0,200 Z;M0,120 C320,60 440,160 720,80 C1000,120 1120,180 1440,80 L1440,200 L0,200 Z;M0,100 C320,180 440,60 720,100 C1000,140 1120,60 1440,100 L1440,200 L0,200 Z" />
          </path>
          <path fill="rgba(255,255,255,0.1)" d="M0,140 C360,80 540,180 720,120 C900,60 1080,160 1440,120 L1440,200 L0,200 Z">
            <animate attributeName="d" dur="10s" repeatCount="indefinite" values="M0,140 C360,80 540,180 720,120 C900,60 1080,160 1440,120 L1440,200 L0,200 Z;M0,100 C360,180 540,80 720,140 C900,180 1080,60 1440,140 L1440,200 L0,200 Z;M0,140 C360,80 540,180 720,120 C900,60 1080,160 1440,120 L1440,200 L0,200 Z" />
          </path>
        </svg>
        {/* Floating decorative elements */}
        <div className="absolute top-[10%] left-[5%] w-32 h-32 rounded-full bg-[#FFB347]/15 blur-2xl animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute top-[30%] right-[8%] w-40 h-40 rounded-full bg-[#FC8EAC]/10 blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute top-[60%] left-[15%] w-24 h-24 rounded-full bg-[#FFD166]/15 blur-2xl animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute bottom-[20%] right-[20%] w-36 h-36 rounded-full bg-[#40E0D0]/10 blur-3xl animate-pulse" style={{ animationDuration: '7s' }} />
      </div>

      {/* Header */}
      <header className="relative z-20 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Treasure Cove" className="h-12 object-contain drop-shadow-lg" />
          </div>
          <div className="flex items-center gap-2">
            {/* Install App Button */}
            {isInstallable && !isInstalled && (
              <Button
                data-testid="install-app-btn"
                onClick={handleInstall}
                variant="ghost"
                className="text-white/80 hover:text-white hover:bg-white/10 border border-white/20 backdrop-blur-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Install App
              </Button>
            )}
            {isInstalled && (
              <span className="flex items-center gap-1.5 text-white/60 text-sm px-3 py-1.5">
                <Check className="w-4 h-4" />
                Installed
              </span>
            )}

            {/* Notification Bell */}
            {pushSupported && (
              <Button
                data-testid="notification-toggle-btn"
                onClick={handleNotificationToggle}
                disabled={pushLoading}
                variant="ghost"
                className={`border backdrop-blur-sm ${
                  isSubscribed 
                    ? 'text-[#FFD166] border-[#FFD166]/30 hover:bg-[#FFD166]/10' 
                    : 'text-white/80 hover:text-white border-white/20 hover:bg-white/10'
                }`}
              >
                {isSubscribed ? <BellRing className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                {pushLoading ? '...' : isSubscribed ? 'Subscribed' : 'Notify Me'}
              </Button>
            )}

            <Button
              data-testid="admin-login-btn"
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="text-white/80 hover:text-white hover:bg-white/10 border border-white/20 backdrop-blur-sm"
            >
              <Lock className="w-4 h-4 mr-2" />
              Admin
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 text-center px-6 pt-12 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md text-white/90 text-sm mb-6 border border-white/20">
            <Waves className="w-4 h-4" />
            Community Magazine
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-4 drop-shadow-lg" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.2)' }}>
            Treasure Cove
          </h1>
          <p className="text-white/85 text-base sm:text-lg max-w-xl mx-auto leading-relaxed drop-shadow" style={{ textShadow: '0 1px 10px rgba(0,0,0,0.15)' }}>
            Stories, culture, and life from our beautiful Bahamian community. Explore our latest editions below.
          </p>
        </div>
      </section>

      {/* Magazine Grid */}
      <section className="relative z-10 px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" style={{ width: '48px', height: '48px', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
            </div>
          ) : magazines.length === 0 ? (
            <div className="text-center py-20 bg-white/10 backdrop-blur-md rounded-3xl border border-white/20">
              <BookOpen className="w-20 h-20 text-white/40 mx-auto mb-4" strokeWidth={1} />
              <h3 className="font-serif text-2xl text-white mb-2">Coming Soon</h3>
              <p className="text-white/70 max-w-md mx-auto">Our first community magazine edition is on the way. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {magazines.map((magazine) => (
                <div
                  key={magazine.id}
                  data-testid={`public-magazine-${magazine.id}`}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/view/${magazine.id}`)}
                >
                  <div className="bg-white/95 backdrop-blur-md rounded-2xl overflow-hidden shadow-xl border border-white/30 transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-2xl">
                    <div className="aspect-[4/3] relative overflow-hidden">
                      {magazine.cover_image ? (
                        <img
                          src={magazine.cover_image}
                          alt={magazine.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#40E0D0] to-[#006994] flex items-center justify-center">
                          <BookOpen className="w-16 h-16 text-white/50" strokeWidth={1} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                        <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 text-sm text-[#006994] font-medium shadow-lg">
                          <Eye className="w-3.5 h-3.5" />
                          Read
                          <ArrowRight className="w-3.5 h-3.5" />
                        </div>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-serif text-xl text-[#0A192F] mb-1 group-hover:text-[#006994] transition-colors line-clamp-2">
                        {magazine.title}
                      </h3>
                      {magazine.description && (
                        <p className="text-sm text-[#4A5568] line-clamp-2 mb-3">
                          {magazine.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-[#4A5568]">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(magazine.updated_at || magazine.created_at), 'MMM d, yyyy')}
                        </span>
                        <span className="text-[#E8E2D2]">|</span>
                        <span>{magazine.article_count || 0} articles</span>
                        <span className="text-[#E8E2D2]">|</span>
                        <span>{magazine.sections?.length || 0} sections</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Treasure Cove" className="h-8 object-contain opacity-80" />
            <span className="text-white/60 text-sm">Treasure Cove Community</span>
          </div>
          <span className="text-white/40 text-xs">{new Date().getFullYear()}</span>
        </div>
      </footer>
      <CenturionFooter />
    </div>
  );
};

export default HomePage;
